import { useMemo } from 'react';

import {
  BookMarkedIcon,
  BookOpenTextIcon,
  CircleDotIcon,
  FileTextIcon,
  GlobeIcon,
  GoalIcon,
  HandIcon,
  LibraryBigIcon,
  MegaphoneIcon,
  MessagesSquareIcon,
  PhoneIcon,
  ShieldIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';
import { ResponseTypes, useMessageNode } from '@/modules/tryIt/hooks/useMessageNode';
import { useResponseTypeFlags } from '@/modules/tryIt/hooks/useResponseTypeFlags';
import { iconByType, TYPES } from '@/redux/dialogs/helper';
import { NodeType, TryItCollection, UserMessage } from '@/shared/types/tryIt';
import { isKeyEnter } from '@/util/util';

const MAX_STEP_ICONS_SHOWN = 5;

// Step-icon ids whose tooltip key doesn't follow the `actions.types.<id>` pattern.
const ICON_TOOLTIP_KEY_OVERRIDES: Record<string, string> = {
  entity: 'common.entity',
  auth: 'actions.types.authentication',
};

const iconType = {
  brain: <CircleDotIcon size={16} />,
  collection: <BookMarkedIcon size={16} />,
  unknown: <CircleDotIcon size={16} />,
  broadcast: <MegaphoneIcon size={16} />,
  campaign: <GoalIcon size={16} />,
  knowledge: <LibraryBigIcon size={16} />,
  voicemail_detected: <PhoneIcon size={16} />,
  interrupted: <HandIcon size={16} />,
  authentication_continue: <ShieldIcon size={16} />,
} as const;

export const datasourceIconByType = {
  website: <GlobeIcon size={16} />,
  zendesk_kb: <BookOpenTextIcon size={16} />,
  intercom_kb: <BookOpenTextIcon size={16} />,
  files: <FileTextIcon size={16} />,
  workvivo: <BookOpenTextIcon size={16} />,
  gdrive: <BookOpenTextIcon size={16} />,
  faq: <MessagesSquareIcon size={16} />,
} as const;

export type MessageNodeHeaderProps = {
  id?: string;
  brainId: string;
  collection?: TryItCollection;
  index: number;
  author_type?: string;
  steps?: NodeType['steps'];
  messages: NodeType['messages'];
  name?: string | null;
  nodes_stack?: NodeType['nodes_stack'];
  broadcastId?: string;
  campaignId?: string;
  broadcast_name?: string;
  campaign_name?: string;
  /** Whether the parent card is active. Icons reveal on active or parent hover. */
  isActive?: boolean;
  /** Redesigned turn row: the step icons alone, for the end of the header row. */
  iconsOnly?: boolean;
};

export const MessageNodeHeader = ({
  id,
  brainId,
  collection,
  index,
  author_type,
  steps,
  messages,
  name,
  nodes_stack,
  broadcastId,
  campaignId,
  broadcast_name,
  campaign_name,
  isActive = false,
  iconsOnly = false,
}: MessageNodeHeaderProps) => {
  const { t } = useTranslation();

  const node = { id, collection, author_type, messages, steps, name, nodes_stack };

  const {
    isBroadcastResponse,
    isCampaignResponse,
    isAgentResponse,
    isBrainResponse,
    isUnknownResponse,
    isCollectionResponse,
    isErrorResponse,
    isInstructionsResponse,
    isInterruptedResponse,
    isVoicemailDetectedResponse,
    isToolResponse,
    isAuthenticationContinueResponse,
    isAIResponse,
  } = useResponseTypeFlags({ index, node });

  const entitiesMatched = (messages?.[0] as UserMessage)?.entities?.length > 0;
  const responseTypeFlags = useMemo(
    () => ({
      isBroadcastResponse,
      isCampaignResponse,
      isAgentResponse,
      isBrainResponse,
      isUnknownResponse,
      isCollectionResponse,
      isErrorResponse,
      isInstructionsResponse,
      isInterruptedResponse,
      isVoicemailDetectedResponse,
      isToolResponse,
      isAuthenticationContinueResponse,
      isAIResponse,
    }),
    [
      isBroadcastResponse,
      isCampaignResponse,
      isAgentResponse,
      isBrainResponse,
      isUnknownResponse,
      isCollectionResponse,
      isErrorResponse,
      isInstructionsResponse,
      isInterruptedResponse,
      isVoicemailDetectedResponse,
      isToolResponse,
      isAuthenticationContinueResponse,
      isAIResponse,
    ]
  );

  const { title, icons, handleGoToNode } = useMessageNode(
    id,
    brainId,
    collection,
    index,
    author_type,
    steps,
    entitiesMatched,
    responseTypeFlags,
    broadcastId,
    campaignId,
    name,
    nodes_stack
  );

  const hasStepIcons = icons?.length > 0 && (isBrainResponse || isCollectionResponse);

  if (iconsOnly && !hasStepIcons) {
    return null;
  }

  return (
    <div
      className={cn(iconsOnly ? 'contents' : 'flex items-start justify-between overflow-hidden')}
    >
      <div
        className={cn(
          'flex max-w-fit min-w-0 flex-[1_0_70%] cursor-pointer items-center gap-1 overflow-hidden text-primary hover:text-primary',
          'py-2',
          iconsOnly && 'hidden',
          (title?.type === ResponseTypes.KNOWLEDGE ||
            title?.type === ResponseTypes.INTERRUPTED ||
            title?.type === ResponseTypes.AUTHENTICATION_CONTINUE) &&
            'cursor-default text-muted-foreground hover:text-muted-foreground'
        )}
        onClick={handleGoToNode}
        onKeyDown={(e) => {
          if (isKeyEnter(e)) {
            handleGoToNode();
          }
        }}
        tabIndex={0}
        role="link"
      >
        {iconType[title?.type]}
        <span className="block max-w-[280px] truncate text-sm leading-5 font-normal">
          {broadcast_name ?? campaign_name ?? title.name}
        </span>
      </div>

      <div
        className={cn(
          'flex flex-wrap items-center justify-end text-muted-foreground',
          !iconsOnly && 'flex-[1_0_30%] gap-2 px-1 py-2',
          !iconsOnly && (isActive ? 'visible' : 'invisible group-hover:visible'),
          // Display, not visibility: hidden icons must free the space for the separator.
          iconsOnly && !isActive && 'hidden group-hover:flex',
          iconsOnly && 'flex-none gap-1'
        )}
      >
        {icons?.length > 0 && (
          <>
            {/* Brain icons */}
            {isBrainResponse && (
              <>
                {icons?.slice(0, MAX_STEP_ICONS_SHOWN).map((icon, iconIndex) => (
                  <InfoTooltip
                    key={`${icon}-${iconIndex}`}
                    content={t(ICON_TOOLTIP_KEY_OVERRIDES[icon] ?? `actions.types.${icon}`)}
                  >
                    <span>
                      {iconByType({
                        id: icon,
                        size: 16,
                        type: TYPES.ACTION,
                        color: 'var(--muted-foreground)',
                        stepsMode: true,
                      })}
                    </span>
                  </InfoTooltip>
                ))}

                {icons?.length > MAX_STEP_ICONS_SHOWN && (
                  <InfoTooltip
                    content={t('try_it.extra_steps', {
                      0: icons.length - MAX_STEP_ICONS_SHOWN,
                      count: icons.length - MAX_STEP_ICONS_SHOWN,
                    })}
                  >
                    <span
                      className={cn(
                        'font-normal',
                        iconsOnly ? 'text-xs leading-4' : 'text-sm leading-5'
                      )}
                    >
                      +{icons.length - MAX_STEP_ICONS_SHOWN}
                    </span>
                  </InfoTooltip>
                )}
              </>
            )}

            {/* Collection icons */}
            {isCollectionResponse && (
              <>
                {icons.map((icon) => (
                  <InfoTooltip key={icon} content={t(`try_it.datasource_icons.${icon}`)}>
                    <span>{datasourceIconByType[icon]}</span>
                  </InfoTooltip>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
